import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { selectUserCounty } from "./app";
import {
  CountyCommissioner,
  CountyConfiguration,
  CountyConfigurationResponse,
  CountyOptions,
  SeasonConfiguration,
  TaxAdministrator,
  TaxBoard,
  UserConfiguration,
} from "../commontypes/types";
import { LoadStatus, SaveStatus } from "../commontypes/common";
import { RootState } from "./index";


type AdminState = {
  countyOptions: CountyOptions;
  taxAdministrator: TaxAdministrator,
  taxBoard: TaxBoard,
  countyCommissioners: CountyCommissioner[],
  meetingRooms: string[],
  valueClassSeason: SeasonConfiguration,
  addOmitSeason: SeasonConfiguration,
  loadStatus: LoadStatus,
  loadError: string,
  saveStatus: SaveStatus,
  saveError: string,
  countyUserConfiguration: UserConfiguration | null,
  countyUserConfigLoadStatus: LoadStatus,
  countyUserConfigLoadError: string,
  countyUserConfigSaveStatus: SaveStatus,
  countyUserConfigSaveError: string,
}
const initialState: AdminState = {
  countyOptions: {
    freezeEvidenceBasedOnOriginalHearingDate: false,
    administratorEmailAddresses: [],
    assessorsMayReviewInterimJudgments: false
  },
  taxAdministrator: {
    name: "",
    title: ""
  },
  taxBoard: {
    name: "",
    address: []
  },
  countyCommissioners: [],
  meetingRooms: [],
  addOmitSeason: {
    season: {},
    municipalityOverrides: []
  },
  valueClassSeason: {
    season: {},
    municipalityOverrides: []
  },
  loadStatus: LoadStatus.NOT_LOADED,
  loadError: "",
  saveStatus: SaveStatus.IDLE,
  saveError: "",
  countyUserConfiguration: null,
  countyUserConfigLoadStatus: LoadStatus.NOT_LOADED,
  countyUserConfigLoadError: "",
  countyUserConfigSaveStatus: SaveStatus.IDLE,
  countyUserConfigSaveError: "",
}

export const fetchCountyConfiguration = createAsyncThunk<
  CountyConfiguration,
  string,
  { state: RootState }
>(
  'admin/fetchCountyConfiguration',
  async (countyId: string, thunkAPI) => {
    const state = thunkAPI.getState();
    const userCounty = selectUserCounty(state);
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${countyId || (userCounty?.key)}/configuration`;
    const response: AxiosResponse<CountyConfigurationResponse> = await axios.get(path);
    return response.data.countyConfiguration as CountyConfiguration;
  }
)

export const saveCountyConfiguration = createAsyncThunk<
  CountyConfiguration,
  CountyConfiguration,
  { state: RootState }
>(
  'admin/saveCountyConfiguration',
  async (countyConfiguration, { getState }) => {
    const state = getState();
    const userCounty = selectUserCounty(state);
    const { countyTaxAdministration, valueClassSeason, addOmitSeason, countyOptions } = countyConfiguration;
    const body = {
      countyTaxAdministration,
      addOmitSeason: addOmitSeason ? addOmitSeason : selectAddOmitSeason(state),
      valueClassSeason: valueClassSeason ? valueClassSeason : selectValueClassSeason(state),
      countyOptions: countyOptions ? countyOptions : selectCountyOptions(state),
    }
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${userCounty?.key}/configuration`;
    const response: AxiosResponse<CountyConfiguration> = await axios.put(path, body);
    return response.data as CountyConfiguration;
  }
)

export const fetchCountyUserConfiguration = createAsyncThunk<
  UserConfiguration,
  string,
  { state: RootState }
>(
  'admin/fetchCountyUserConfiguration',
  async (uuid, { getState }) => {
    const state = getState();
    const userCounty = selectUserCounty(state);
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${userCounty?.key}/userconfig`;
    const response: AxiosResponse<{ userConfiguration: UserConfiguration }> = await axios.get(path, { params: { UUID: uuid } });
    return response.data.userConfiguration;
  }
)

export const saveCountyUserConfiguration = createAsyncThunk<
  UserConfiguration,
  UserConfiguration,
  { state: RootState }
>(
  'admin/saveCountyUserConfiguration',
  async (userConfiguration, { getState }) => {
    const state = getState();
    const userCounty = selectUserCounty(state);
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${userCounty?.key}/userconfig`;
    const response: AxiosResponse<{ userConfiguration: UserConfiguration }> = await axios.put(path, userConfiguration);
    return response.data.userConfiguration;
  }
)

export const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearState: () => initialState,
    clearCountyConfiguration: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCountyConfiguration.pending, (state) => {
      state.loadStatus = LoadStatus.IN_PROGRESS;
      state.loadError = "";
    })
      .addCase(fetchCountyConfiguration.rejected, (state, action) => {
        state.loadStatus = LoadStatus.FAILED;
        state.loadError = action.error.message!;
      })
      .addCase(fetchCountyConfiguration.fulfilled, (state, action) => {
        const config = action.payload;
        if (config) {
          state.taxAdministrator = config.countyTaxAdministration.taxAdministrator;
          state.taxBoard = config.countyTaxAdministration.taxBoard;
          state.meetingRooms = config.countyTaxAdministration.meetingRooms;
          state.countyCommissioners = config.countyTaxAdministration.countyCommissioners;
          state.addOmitSeason = config.addOmitSeason!;
          state.valueClassSeason = config.valueClassSeason!;
          if (config.countyOptions) state.countyOptions = config.countyOptions;
        }
        state.loadStatus = LoadStatus.SUCCESS;
      })
      .addCase(saveCountyConfiguration.pending, (state) => {
        state.saveStatus = SaveStatus.IN_PROGRESS;
        state.saveError = "";
      })
      .addCase(saveCountyConfiguration.rejected, (state, action) => {
        state.saveStatus = SaveStatus.FAILED;
        state.saveError = action.error.message!;
      })
      .addCase(saveCountyConfiguration.fulfilled, (state) => {
        state.saveStatus = SaveStatus.SUCCESS;
        state.saveError = "";
      })
      .addCase(fetchCountyUserConfiguration.pending, (state) => {
        state.countyUserConfigLoadStatus = LoadStatus.IN_PROGRESS;
        state.countyUserConfigLoadError = "";
        state.countyUserConfiguration = null;
      })
      .addCase(fetchCountyUserConfiguration.rejected, (state, action) => {
        state.countyUserConfigLoadStatus = LoadStatus.FAILED;
        state.countyUserConfigLoadError = action.error.message!;
      })
      .addCase(fetchCountyUserConfiguration.fulfilled, (state, action) => {
        state.countyUserConfiguration = action.payload;
        state.countyUserConfigLoadStatus = LoadStatus.SUCCESS;
        state.countyUserConfigLoadError = "";
      })
      .addCase(saveCountyUserConfiguration.pending, (state) => {
        state.countyUserConfigSaveStatus = SaveStatus.IN_PROGRESS;
        state.countyUserConfigSaveError = "";
      })
      .addCase(saveCountyUserConfiguration.rejected, (state, action) => {
        state.countyUserConfigSaveStatus = SaveStatus.FAILED;
        state.countyUserConfigSaveError = action.error.message!;
      })
      .addCase(saveCountyUserConfiguration.fulfilled, (state, action) => {
        state.countyUserConfiguration = action.payload;
        state.countyUserConfigSaveStatus = SaveStatus.SUCCESS;
        state.countyUserConfigSaveError = "";
      })
  }
});

export const selectCountyOptions = (state: RootState) => state.admin.countyOptions;
export const selectTaxAdministrator = (state: RootState) => state.admin.taxAdministrator;
export const selectTaxBoard = (state: RootState) => state.admin.taxBoard;
export const selectMeetingRooms = (state: RootState) => state.admin.meetingRooms;
export const selectCountyCommissioners = (state: RootState) => state.admin.countyCommissioners
export const selectValueClassSeason = (state: RootState) => state.admin.valueClassSeason;
export const selectAddOmitSeason = (state: RootState) => state.admin.addOmitSeason;
export const selectCountyConfigLoadStatus = (state: RootState) => state.admin.loadStatus;
export const selectCountyConfigLoadError = (state: RootState) => state.admin.loadError;
export const selectCountyConfigSaveStatus = (state: RootState) => state.admin.saveStatus;
export const selectCountyConfigSaveError = (state: RootState) => state.admin.saveError;
export const selectCountyUserConfiguration = (state: RootState) => state.admin.countyUserConfiguration;
export const selectCountyUserConfigLoadStatus = (state: RootState) => state.admin.countyUserConfigLoadStatus;
export const selectCountyUserConfigLoadError = (state: RootState) => state.admin.countyUserConfigLoadError;
export const selectCountyUserConfigSaveStatus = (state: RootState) => state.admin.countyUserConfigSaveStatus;
export const selectCountyUserConfigSaveError = (state: RootState) => state.admin.countyUserConfigSaveError;
export const { clearCountyConfiguration } = adminSlice.actions;
export default adminSlice.reducer;



